import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * EWM-1100: the last-resort boundary, above the provider tree.
 *
 * The router already has one - `defaultErrorComponent={GenericError}` - and it covers everything
 * rendered inside a route. It cannot cover what renders above it. In both apps the tree is
 * InfraProviders -> DataProviders -> AppProviders -> RouterProvider, so a throw in any of the
 * thirteen providers happens outside the router's boundary: React unmounts the whole tree, the
 * user gets a white screen, and nothing reports it. EWM-1081 was exactly that, and its own comment
 * says so - "a white screen with no message and no UI path to recover", "a SyntaxError no error
 * boundary caught". That bug was fixed at the read seam; the hole it came through is still open.
 *
 * This fallback deliberately depends on nothing:
 *
 *   - No hooks and no context. GenericError cannot be reused here, because it calls `useMessages`
 *     and `useShowNewVersionDeployedOnce`. If LocalizationProvider is what threw, rendering it
 *     would throw again inside the boundary and white-screen anyway.
 *   - No message catalogue. The copy is inlined for both supported locales and the locale is read
 *     straight from storage, defensively - shipping an English-only crash screen to a German user
 *     is the failure this codebase has spent a month fixing elsewhere.
 *   - No Tailwind utility classes. The stylesheet import sits above this in main.tsx, so a fallback
 *     that needs CSS is a fallback that is invisible in the one case CSS did not load.
 *
 * `reportCrash` is injected rather than imported: the sink lives per app
 * (`@/services/crashReporting` in each), and this component sits in shared code that must not
 * reach into either.
 */

type ReportCrash = (error: Error, context: { source: 'react-root-boundary'; componentStack?: string }) => void

interface Props {
  children: ReactNode
  reportCrash: ReportCrash
  /** Overridable for tests; production reads the same key the localization context writes. */
  localeStorageKey?: string
}

interface State {
  error: Error | null
}

interface Copy {
  title: string
  body: string
  action: string
}

const COPY: Record<'de-DE' | 'en-GB', Copy> = {
  'de-DE': {
    title: 'Es ist ein unerwarteter Fehler aufgetreten',
    body: 'Die Anwendung konnte nicht geladen werden. Bitte laden Sie die Seite neu.',
    action: 'Seite neu laden',
  },
  'en-GB': {
    title: 'Something went wrong',
    body: 'The application could not be loaded. Please reload the page.',
    action: 'Reload the page',
  },
}

/**
 * Best-effort locale read. Anything unexpected falls back to English rather than throwing - a
 * boundary that can fail while rendering its own fallback is not a boundary.
 */
const resolveCopy = (storageKey: string): Copy => {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return COPY['en-GB']
    // The value is JSON-encoded ("de-DE"), but EWM-1081 proved it is not always well-formed.
    const parsed = raw.startsWith('"') ? (JSON.parse(raw) as unknown) : raw
    return parsed === 'de-DE' ? COPY['de-DE'] : COPY['en-GB']
  } catch {
    return COPY['en-GB']
  }
}

export class RootErrorBoundary extends Component<Props, State> {
  public constructor(props: Props) {
    super(props)
    this.state = { error: null }
  }

  public static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    // Wrapped: if the sink itself is broken there is nothing above this to catch it, and failing
    // here would replace a rendered error screen with the white screen this exists to prevent.
    try {
      this.props.reportCrash(error, {
        source: 'react-root-boundary',
        componentStack: info.componentStack ?? undefined,
      })
    } catch {
      // Deliberately swallowed. The fallback below is still shown.
    }
  }

  public override render(): ReactNode {
    const { error } = this.state
    if (!error) {
      return this.props.children
    }

    const copy = resolveCopy(this.props.localeStorageKey ?? 'language_i18')

    return (
      <div
        role='alert'
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          padding: '2rem',
          fontFamily: 'system-ui, sans-serif',
          textAlign: 'center',
          background: '#0f172a',
          color: '#f8fafc',
        }}
      >
        <h1 style={{ fontSize: '1.25rem', margin: 0 }}>{copy.title}</h1>
        <p style={{ margin: 0, opacity: 0.8 }}>{copy.body}</p>
        <button
          type='button'
          onClick={() => window.location.reload()}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            border: '1px solid #f8fafc',
            background: 'transparent',
            color: '#f8fafc',
            cursor: 'pointer',
          }}
        >
          {copy.action}
        </button>
      </div>
    )
  }
}
